/**
 * Per-athlete status metadata for the Athletes list and detail pages.
 *
 * Extracted from athleteClientService (#2669) — it was the largest thing in a
 * file that had grown past its line budget, and it is a self-contained read.
 * Every `.in()` here is chunked: callers pass the WHOLE roster, which used to
 * overflow the request URL on big gyms.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import { fetchInChunks, fetchAllInChunks } from '@/utils/supabase/fetchInChunks';
import { fetchArchiveReasons } from '@/services/athlete/archiveReasonMetadata';

const logger = createLogger('AthleteStatusMetadata');

/**
 * Status metadata per athlete used by getAthleteEffectiveStatus/isNewAthlete:
 * - enrollmentDate: when the athlete joined this org (athlete_organizations.created_at)
 * - accountSource: auth_users.account_source via athletes.auth_user_id
 *   ('public_signup' for self-service; other values for staff-created / imports)
 * - lastParticipationEndedAt: most recent end across team_athletes, class_enrollments,
 *   athlete_location_memberships, and completed private_lesson_bookings. Powers the
 *   60-day grace window that keeps returning athletes Active through the off-season.
 * - hasWaitlistEntry: athlete currently holds a waitlist spot in this org. There are
 *   TWO parallel waitlist constructs and both must be checked: the program_waitlist
 *   table (statuses 'waiting'/'offered' are live; 'claimed'/'expired'/'removed'/
 *   'declined' are not) AND class_enrollments rows with status 'waitlist'. Drives the
 *   "Waitlisted" pill (isWaitlistedAthlete) — display only, never billing/capacity.
 * - organizationStatus: athlete_organizations.status for THIS org. Feeds
 *   getAthleteEffectiveStatus's orgStatus (manual archive beats grace #830,
 *   prospect/suspended labels) — the list previously never loaded it, so those
 *   priority rules silently couldn't fire on list rows (#1629).
 * - archiveReason* / archivedBySystem: the most recent athlete_status_history
 *   row that set this athlete inactive, attached ONLY while organizationStatus
 *   is still 'inactive' (a reactivated athlete's old archive reason is history,
 *   not a current label). archivedBySystem=true means the nightly status job
 *   (#835) flipped the flag — a lapse, not a staff archive; null means no
 *   history row exists. Drives the archived-vs-lapsed split (#1629).
 */
export interface AthleteStatusMetadata {
  enrollmentDate: string | null;
  accountSource: string | null;
  lastParticipationEndedAt: string | null;
  hasWaitlistEntry: boolean;
  organizationStatus: string | null;
  archiveReasonCategory: string | null;
  archiveReasonDetails: string | null;
  archivedAt: string | null;
  archivedBySystem: boolean | null;
}

export async function fetchAthleteStatusMetadata(
  athleteIds: string[],
  organizationId: string,
  client: SupabaseClient = supabase
): Promise<Map<string, AthleteStatusMetadata>> {
  const result = new Map<string, AthleteStatusMetadata>();
  if (!athleteIds.length) return result;

  const blank = (): AthleteStatusMetadata => ({
    enrollmentDate: null,
    accountSource: null,
    lastParticipationEndedAt: null,
    hasWaitlistEntry: false,
    organizationStatus: null,
    archiveReasonCategory: null,
    archiveReasonDetails: null,
    archivedAt: null,
    archivedBySystem: null,
  });
  athleteIds.forEach(id => result.set(id, blank()));

  // Coerce any date/timestamp string to a YYYY-MM-DD calendar-day string so
  // the lexicographic max comparisons below are correct across mixed formats
  // (some columns are DATE = "2026-04-10", others are TIMESTAMPTZ =
  // "2026-04-10T14:23:00+00"). Without this, same-day compares pick the wrong
  // winner.
  const toDateOnly = (value: string | null | undefined): string | null => {
    if (!value) return null;
    // Already date-only
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // ISO timestamp — take the YYYY-MM-DD prefix
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  const updateMax = (id: string, dateStr: string | null | undefined) => {
    const normalized = toDateOnly(dateStr);
    if (!normalized) return;
    const row = result.get(id);
    if (!row) return;
    if (!row.lastParticipationEndedAt || normalized > row.lastParticipationEndedAt) {
      row.lastParticipationEndedAt = normalized;
    }
  };

  try {
    // Every read here is chunked: this runs with the whole roster's ids, which
    // blew past the URL length limit on big gyms (#2669). The per-athlete-HISTORY
    // reads additionally page each chunk — 100 athletes' worth of ended
    // enrollments or completed lessons can exceed PostgREST's 1,000-row cap,
    // which truncates silently and would quietly compute status from partial
    // history. The one-row-per-athlete reads need chunking only.
    const [enrollmentRes, athleteRes, teamRes, classRes, membershipRes, lessonRes, programWaitlistRes, classWaitlistRes] = await Promise.all([
      fetchInChunks<any>(athleteIds, (chunk) => client
        .from('athlete_organizations')
        .select('athlete_id, created_at, status')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)),
      fetchInChunks<any>(athleteIds, (chunk) => client
        .from('athletes')
        .select('id, auth_user_id')
        .in('id', chunk)),
      // team_athletes has no organization_id column, so we join through teams
      // and scope by teams.organization_id. Otherwise a multi-gym athlete's
      // end dates from other gyms would pollute this org's grace-window math.
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('team_athletes')
        .select('athlete_id, end_date, teams!inner(organization_id)')
        .eq('teams.organization_id', organizationId)
        .in('athlete_id', chunk)
        .not('end_date', 'is', null)
        .order('id', { ascending: true })
        .range(from, to)),
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('class_enrollments')
        .select('athlete_id, end_date')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)
        .not('end_date', 'is', null)
        // Waitlist rows carry the class session's end_date but were never
        // participation — they must not extend the 60-day grace window
        // (a waitlist spot is not participation; see #837).
        .neq('status', 'waitlist')
        .order('id', { ascending: true })
        .range(from, to)),
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('athlete_location_memberships')
        .select('athlete_id, membership_end_date, cancelled_at, membership_status')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)
        .neq('membership_status', 'active')
        .order('id', { ascending: true })
        .range(from, to)),
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('private_lesson_bookings')
        .select('athlete_id, lesson_date')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)
        .eq('status', 'completed')
        .order('id', { ascending: true })
        .range(from, to)),
      // Live waitlist construct #1: the program_waitlist table.
      // 'waiting' and 'offered' are current spots; claimed/expired/removed/
      // declined are history and must not light up the Waitlisted pill.
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('program_waitlist')
        .select('athlete_id')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)
        .in('status', ['waiting', 'offered'])
        .order('id', { ascending: true })
        .range(from, to)),
      // Live waitlist construct #2: class_enrollments rows parked in
      // status 'waitlist' (parallel system — both must be checked).
      fetchAllInChunks<any>(athleteIds, (chunk, from, to) => client
        .from('class_enrollments')
        .select('athlete_id')
        .eq('organization_id', organizationId)
        .in('athlete_id', chunk)
        .eq('status', 'waitlist')
        .order('id', { ascending: true })
        .range(from, to)),
    ]);

    enrollmentRes.rows.forEach((row: any) => {
      const rec = result.get(row.athlete_id);
      if (!rec) return;
      if (row.created_at) rec.enrollmentDate = toDateOnly(row.created_at);
      rec.organizationStatus = row.status ?? null;
    });

    const authIdToAthlete = new Map<string, string>();
    athleteRes.rows.forEach((row: any) => {
      if (row.auth_user_id) authIdToAthlete.set(row.auth_user_id, row.id);
    });
    const authUserIds = Array.from(authIdToAthlete.keys());
    if (authUserIds.length) {
      const { rows: authUsers } = await fetchInChunks<any>(authUserIds, (chunk) => client
        .from('auth_users')
        .select('id, account_source')
        .in('id', chunk));
      authUsers.forEach((u: any) => {
        const athleteId = authIdToAthlete.get(u.id);
        if (athleteId) {
          const rec = result.get(athleteId);
          if (rec) rec.accountSource = u.account_source ?? null;
        }
      });
    }

    teamRes.rows.forEach((r: any) => updateMax(r.athlete_id, r.end_date));
    classRes.rows.forEach((r: any) => updateMax(r.athlete_id, r.end_date));
    membershipRes.rows.forEach((r: any) => {
      // Both fields may be a DATE or a TIMESTAMPTZ — normalize before picking
      // the later one so we don't get fooled by format prefix-matching.
      const endDay = toDateOnly(r.membership_end_date);
      const cancelledDay = toDateOnly(r.cancelled_at);
      let latest: string | null = null;
      if (endDay && cancelledDay) {
        latest = endDay > cancelledDay ? endDay : cancelledDay;
      } else {
        latest = endDay || cancelledDay;
      }
      updateMax(r.athlete_id, latest);
    });
    lessonRes.rows.forEach((r: any) => updateMax(r.athlete_id, r.lesson_date));

    const markWaitlisted = (r: any) => {
      const rec = result.get(r.athlete_id);
      if (rec) rec.hasWaitlistEntry = true;
    };
    programWaitlistRes.rows.forEach(markWaitlisted);
    classWaitlistRes.rows.forEach(markWaitlisted);

    // Archive reason for currently-archived athletes only — runs AFTER the
    // org rows above so it can scope to just the archived subset.
    const archivedIds = athleteIds.filter(id => result.get(id)?.organizationStatus === 'inactive');
    const reasons = await fetchArchiveReasons(archivedIds, organizationId, client, toDateOnly);
    reasons.forEach((reason, athleteId) => Object.assign(result.get(athleteId) ?? {}, reason));
  } catch (error) {
    logger.error('Error loading athlete status metadata:', error);
  }

  return result;
}
